import Logger from '@utils/log'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import ParseAPI from '@api/ParseAPI'
import {
    RECORD_TYPES,
    RECORD_TYPE_CHECK,
    RECORD_TYPE_DOCUMENT,
} from '@constants/annotations'
import {
    DOCUMENT_STATUS_MODEL_APPLIED,
} from '@constants'
import { PENDO_EVENTS, pendoTrack } from '@utils/pendo'
import {
    formatAnnotationForLabelStudio,
    initializeLabelStudio,
    getVisibleLabels,
} from '@utils/label-studio'
import { isEqual, waitFor } from '@utils'

import { toast } from 'react-toastify'
import '@parse-ai/label-studio/build/static/css/main.css'
import '@utils/label-studio/label-studio.css'
import { useGuardExit } from './useGuardExit'

const log = new Logger('hook/useLabelStudio')

export const ANNOTATION_TYPE_MAP = {
    [RECORD_TYPE_DOCUMENT]: {
        recordEndpoint: id => `/document/${id}`,
        recordsEndpoint: '/documents',
        annotationsEndpoint: '/annotations',
        pageIdKey: 'pageId',
        pendoEvent: PENDO_EVENTS.DOC_ANNOTATE,
    },
    [RECORD_TYPE_CHECK]: {
        recordEndpoint: id => `/checks/${id}?withAnnotations=true&withAnnotationLabels=true`,
        recordsEndpoint: '/checks',
        annotationsEndpoint: '/checkannotations',
        pageIdKey: 'checkPageId',
        pendoEvent: PENDO_EVENTS.CHECK_ANNOTATE,
    },
}

export const useLabelStudio = ({
    enabled,
    url,
    gcsBucket,
    editorNode,
    record,
    recordType,
    pageIndex,
    setRecord,
    showAllLabels = false,
    showPrivateLabels = false,
    useOrganizationLabels = true,
    useCollectionLabels = true,
} = {}) => {
    
    if (!recordType || !Object.values(RECORD_TYPES).includes(recordType))
        throw new Error(`
            Invalid "recordType": "${recordType}".
            Must be one of ${Object.keys(RECORD_TYPES).join(', ')}`)
    
    const [loading, setLoading] = useState(true)
    const [labelStudioError, setLabelStudioError] = useState(null)
    const [isFetchingLabels, setIsFetchingLabels] = useState(false)
    const [labels, setLabels] = useState(null)
    const [organizationLabels, setOrganizationLabels] = useState(null)
    const [collectionLabels, setCollectionLabels] = useState(null)
    const [isSaving, setIsSaving] = useState(false)
    const [annotationExists, setAnnotationExists] = useState(true)
    
    const verifyUnsavedChanges = useCallback(() => {
        const mapToTruncateValues = el => {
            const copy = { ...el }
            
            // if its a relation theres no value
            if (el.value)
                copy.value = Object.fromEntries(
                    Object.entries(el.value).map(([key, val]) => [key, Math.trunc(val)]),
                )
        }
        
        const serialized = window.app?.ls?.store?.annotationStore?.annotations?.[0].serializeAnnotation()
        const previousData = window.app?.ls?.serialized?.filter(s => !!s)
        
        if (!previousData || !serialized) return false
        
        const serializedWithTruncateValues = serialized.map(mapToTruncateValues)
        const previousDataWithTruncateValues = previousData.map(mapToTruncateValues)
        
        return !isEqual(serializedWithTruncateValues, previousDataWithTruncateValues)
    }, [])
    
    useGuardExit(verifyUnsavedChanges, 'Are you sure you want to leave? You have unsaved changes.')
    
    const showExpandedAnnotationView = useWireValue(store.showExpandedAnnotationView)
    
    //region Memos
    
    const hasOrganizationLabels = useMemo(() => (
        organizationLabels?.length > 0
    ), [organizationLabels])
    
    const hasCollectionLabels = useMemo(() => (
        collectionLabels?.length > 0
    ), [collectionLabels])
    
    const hasAssignedLabels = useMemo(() => (
        hasOrganizationLabels &&
        hasCollectionLabels
    ), [
        hasOrganizationLabels,
        hasCollectionLabels,
    ])
    
    const usedLabels = useMemo(() => {
        
        if (isFetchingLabels || !labels || !organizationLabels || !collectionLabels)
            return null
        
        return getVisibleLabels({
            labels,
            organizationLabels,
            collectionLabels,
            recordType,
            showAllLabels,
            showPrivateLabels,
        })
        
    }, [
        isFetchingLabels,
        labels,
        organizationLabels,
        collectionLabels,
        recordType,
        showAllLabels,
        showPrivateLabels,
    ])
    
    //endregion Memos
    
    //region Label Fetching
    
    const fetchCoreLabels = useCallback(async () => {
        
        try {
            
            const labelsRes = await actions.fetchLabels(recordType, {
                documentId: record.id,
            })
            
            setLabels(labelsRes?.data?.length ? labelsRes.data : [])
            
        } catch (e) {
            
            console.error('fetchLabels', e)
            
        }
        
    }, [recordType, record])
    
    const fetchOrganizationLabels = useCallback(async organizationId => {
        
        try {
            
            const res = await actions.fetchOrgWithLabels(organizationId)
            
            setOrganizationLabels(res?.labels?.length ? res.labels : [])
            
        } catch (e) {
            
            console.error('fetchOrganizationLabels', e)
            
        }
        
    }, [])
    
    const fetchCollectionLabels = useCallback(async collectionId => {
        
        try {
            
            const res = await actions.fetchCollectionById(collectionId)
            
            setCollectionLabels(res?.data?.labels?.length ? res.data.labels : [])
            
        } catch (e) {
            
            console.error('fetchCollectionLabels', e)
            
        }
        
    }, [])
    
    const fetchLabels = useCallback(async ({
        organizationId,
        collectionId,
    } = {}) => {
        
        setIsFetchingLabels(true)
        
        const promises = [fetchCoreLabels()]
        
        if (useOrganizationLabels && organizationId)
            promises.push(fetchOrganizationLabels(organizationId))
        else
            setOrganizationLabels([])
        
        if (useCollectionLabels && collectionId)
            promises.push(fetchCollectionLabels(collectionId))
        else
            setCollectionLabels([])
        
        const result = await Promise.all(promises)
        
        setIsFetchingLabels(false)
        
        return result
        
    }, [
        fetchCoreLabels,
        useOrganizationLabels,
        fetchOrganizationLabels,
        useCollectionLabels,
        fetchCollectionLabels,
    ])
    
    //endregion Label Fetching
    
    //region Methods
    
    // Updates the local record after saving annotations, if the `setRecord` property was given
    const onSaveComplete = useCallback(async pageId => {
        
        log.d('onSaveComplete', { pageId })
        
        const typeMap = ANNOTATION_TYPE_MAP[recordType]
        
        // After saving annotations, we need to update the record
        const res = await ParseAPI.get(`${typeMap.recordEndpoint(record.id)}`)
        
        // @todo prob need some better error checking here
        log.d('updatedDoc', res.data)
        
        const foundPage = res.data?.pages?.find(it => it.id === pageId)
        
        if (!foundPage) {
            
            log.w('Could not update local page', pageId, 'after saving annotations')
            
            // Something really went wrong & we didn't get the record from the API
            if (!res.data)
                throw new Error('Updated record could not be fetched')
            
            // The page couldn't be found, but just update the whole record anyway
            setRecord(res.data)
            
        } else {
            
            log.d('Found local page to update', { pageId, foundPage, pages: record.pages })
            
            // The updated page was found, so just update that page, so we don't repaint everything
            setRecord({
                ...record,
                pages: record.pages.map(page => {
                    if (page.id === pageId)
                        return foundPage
                    return page
                }),
            })
            
        }
        
    }, [recordType, record, setRecord])
    
    /**
     * Receives the LS payload of updated annotations & sends it to the API to be saved
     * 
     * @note the [record, page] dependencies here are critical,
     * or it will save to the wrong page in the database
     */
    const onUpdateAnnotation = useCallback(async (ls, annotation, serialized) => {
        
        log.d('onUpdateAnnotation', JSON.stringify({ serialized }, null, 4))
        actions.forgetHasPromptedToProcessDocumentId(record.id)
        
        setIsSaving(true)
        
        try {
            
            const pageId = record.pages.find(it => it.pageIndex === pageIndex).id
            const typeMap = ANNOTATION_TYPE_MAP[recordType]
            
            const res = await ParseAPI.post(typeMap.annotationsEndpoint, {
                [typeMap.pageIdKey]: pageId,
                annotationType: 'annotator',
                annotationResults: serialized,
            })
            
            pendoTrack(typeMap.pendoEvent, {
                id: record.id,
                [typeMap.pageIdKey]: pageId,
            })
            
            // xxx-eslint-disable-next-line react-compiler/react-compiler
            window.app.ls.serialized = serialized
            
            log.d('onUpdateAnnotation success', res)
            
            if (setRecord)
                await onSaveComplete(pageId)
            
            serialized.sort((a, b) => {
                if (a.value?.y === b.value?.y)
                    return a.value?.x - b.value?.x
                return a.value?.y - b.value?.y
            })
            
            annotation.deleteAllRegions()
            annotation.deserializeResults(serialized)
            
            toast.success('Annotations saved')
            
        } catch (e) {
            
            log.e('onUpdateAnnotation', e)
            
        }
        
        setIsSaving(false)
        
    }, [
        record,
        setRecord,
        recordType,
        pageIndex,
        onSaveComplete,
    ])
    
    const unloadLabelStudio = useCallback(async () => {
        
        try {
            
            console.log('Unloading Label Studio')
            
            // forcefully unbind all keys from label studio and destroy any existing instance
            window.HtxHotkeys.unbindAll()
            window.LabelStudio.destroyAll()
            
            // this might not be necessary anymore
            if (editorNode?.childNodes?.length > 0) {
                
                const res = await waitFor(
                    () => editorNode ? editorNode.childNodes.length === 0 : true,
                    1000,
                    3,
                )
                
                if (!res)
                    console.warn('Could not reliably unload Label Studio')
                
            }
            
        } catch (e) {
            
            // NOOP
            
        }
        
    }, [editorNode])
    
    const initialize = useCallback(async () => {
        
        if (!enabled || !editorNode || !record)
            return console.warn('initialize not enabled or no editor ref', {
                enabled,
                ref: editorNode,
            })
        
        // Don't infinite loop, please
        if (isFetchingLabels) return
        
        if (!usedLabels) {
            fetchLabels({
                organizationId: record.organizationId,
                collectionId: record.collectionId,
            })
            return
        }
        
        try {
            
            if (!record.pages.length) {
                console.error('No pages found for record', record)
                return setLabelStudioError('No pages found for record')
            }
            
            const page = record.pages.find(it => it.pageIndex === pageIndex)
            const sortedAnnotations = page.annotations?.sort((a, b) => {
                if (a.updatedAt > b.updatedAt) return -1
                if (a.updatedAt <= b.updatedAt) return 1
                return 0
            }) || []
            
            // passing an empty result object is enough for label studio to render
            const pageAnnotation = sortedAnnotations?.[0]?.result?.length > 0
                ? sortedAnnotations[0]
                : { result: [{}] }
            
            setAnnotationExists(
                record.orgStatuses.some(orgStatus => orgStatus.status === DOCUMENT_STATUS_MODEL_APPLIED) 
            || record.pages.some(page => page.annotations?.length),
            )
            
            // Need to clone record here, otherwise `formatAnnotationForLabelStudio` strips information we need
            const annotationsCopy = JSON.parse(JSON.stringify(pageAnnotation))
            
            const removableLabelIds = new Set()
            
            annotationsCopy.result.forEach(r => {
                if (r.type !== 'labels') return
                
                if (r.value.labels && !usedLabels.some(ul => ul.label === r.value.labels[0]))
                    removableLabelIds.add(r.id)
            })
            
            annotationsCopy.result = annotationsCopy.result.filter(r => !removableLabelIds.has(r.id))
            
            // LS won't load without at least 1 annotation result,
            // and after we've filtered for labels matching our config,
            // there might be no results left, so stub one if needed
            if (!annotationsCopy.result.length)
                annotationsCopy.result = [{}]
            
            const task = await formatAnnotationForLabelStudio(
                gcsBucket,
                page.fileName,
                url,
                annotationsCopy,
            )
            
            // console.log('Initialize unloading ls')
            await unloadLabelStudio()
            
            log.d('Initialize LabelStudio', {
                url,
                pageIndex,
                page,
                annotationsCopy,
                usedLabels,
                record,
                predictionCount: task.predictions?.[0]?.result?.length ?? 0,
                task,
                usedLabelsCount: usedLabels.length,
            })
            
            // initializeWithAnnotations(annotations)
            const instance = initializeLabelStudio(
                task,
                usedLabels,
                onUpdateAnnotation,
                null,
                () => setLoading(false),
            )
            
            window.app = window.app || {}
            window.app.ls = instance
            window.app.ls.serialized = task.predictions[0].result
            
        } catch (e) {
            
            console.error('useLabelStudio effect', e)
            
            setLabelStudioError('An unexpected error occurred')
            
        }
        
    }, [
        enabled,
        editorNode,
        record,
        isFetchingLabels,
        usedLabels,
        fetchLabels,
        pageIndex,
        gcsBucket,
        url,
        unloadLabelStudio,
        onUpdateAnnotation,
    ])
    
    //endregion Methods
    
    //region Hooks
    
    useEffect(() => {
        
        // eslint-disable-next-line no-restricted-globals
        const updateButton = document.querySelector(
            '.lsf-controls .lsf-button.lsf-button_look_primary[aria-label="submit"]')
        
        if (!updateButton) return
        
        updateButton.disabled = isSaving
        updateButton.style.opacity = isSaving ? '0.3' : '1.0'
        
    }, [isSaving])
    
    useEffect(() => {
        
        if (!enabled) {
            unloadLabelStudio()
            return
        }
        
        initialize()
        
        return () => {
            unloadLabelStudio()
        }
        
        
    }, [
        enabled,
        url,
        editorNode,
        pageIndex,
        usedLabels,
        showAllLabels,
        showPrivateLabels,
        showExpandedAnnotationView,
        
        // @todo adding these causes an infinite loading loop
        // initialize,
        // unloadLabelStudio,
    ])
    
    //endregion Hooks
    
    return {
        labelStudioLoading: loading,
        labelStudioSaving: isSaving,
        labelStudioError,
        unloadLabelStudio,
        hasOrganizationLabels,
        hasCollectionLabels,
        hasAssignedLabels,
        labels,
        organizationLabels,
        collectionLabels,
        usedLabels,
        annotationExists,
        verifyUnsavedChanges,
    }
    
}
